import { Search, Wrench } from "lucide-react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { ORDER_STAGES } from "./gulaConstants";
import { fullName, type GulaInstaller, type GulaOrder } from "./gulaTypes";
import { KanbanBoard } from "./KanbanBoard";
import { madridDay, nextWorkingDay } from "./deliveryDates";
import { isAwaitingDelivery } from "./deliveryStatus";
import { OrderCard, type OrderAction } from "./OrderCard";
import { ReceivedDialog } from "./ReceivedDialog";

// Timestamp recorded when an order reaches each stage
const STAGE_TIMESTAMP: Record<string, keyof GulaOrder> = {
  shipped: "shipped_at",
  received: "received_at",
  installer_notified: "installer_notified_at",
  installed: "installed_at",
};

export const OperationsPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [query, setQuery] = useState("");
  const [receivedOrder, setReceivedOrder] = useState<GulaOrder | null>(null);

  const {
    data = [],
    refetch,
    isPending,
  } = useGetList<GulaOrder>(
    "gula_orders_summary",
    {
      filter: { "stage@neq": "cancelled" },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "stage_changed_at", order: "ASC" },
    },
    // Packlink updates arrive server side: show them without reloading
    { refetchInterval: 60_000 },
  );
  const { data: installers = [] } = useGetList<GulaInstaller>(
    "gula_installers",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const orders = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return data;
    return data.filter((order) =>
      [
        fullName(order),
        order.phone,
        order.city,
        order.zone,
        order.installer_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle)),
    );
  }, [data, query]);

  const handleAction: OrderAction = async (order, toStage, extra = {}) => {
    const timestampField = STAGE_TIMESTAMP[toStage];
    const data: Record<string, unknown> = { stage: toStage, ...extra };
    // Shipped by hand or dragged: assume next working day until someone sets it
    if (
      toStage === "shipped" &&
      order.stage !== "shipped" &&
      !order.expected_delivery_date &&
      !data.expected_delivery_date
    ) {
      data.expected_delivery_date = nextWorkingDay(madridDay());
    }
    if (timestampField && toStage !== order.stage && !order[timestampField]) {
      data[timestampField] = new Date().toISOString();
    }
    try {
      await dataProvider.update("gula_orders", {
        id: order.id,
        data,
        previousData: { id: order.id },
      });
      await refetch();
    } catch (error) {
      console.error(error);
      notify("No se ha podido actualizar el pedido", { type: "error" });
    }
  };

  const pendingInstall = data.filter((order) =>
    ["received", "installer_notified"].includes(order.stage),
  ).length;
  const awaitingDelivery = data.filter(isAwaitingDelivery).length;

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Operaciones</h1>
          <p className="text-sm text-muted-foreground">
            {data.filter((order) => order.stage === "to_ship").length} por
            enviar · {awaitingDelivery} sin entregar todavía · {pendingInstall}{" "}
            pendientes de instalar
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar cliente, zona, instalador…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-64 pl-8"
            />
          </div>
          <Button variant="outline" asChild>
            <Link to="/instaladores">
              <Wrench className="h-4 w-4" />
              Instaladores
            </Link>
          </Button>
        </div>
      </div>
      {isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : (
        <KanbanBoard
          columns={ORDER_STAGES}
          items={orders}
          onMove={(order, toStage) =>
            toStage === "received"
              ? setReceivedOrder(order)
              : handleAction(order, toStage)
          }
          renderCard={(order) => (
            <OrderCard
              order={order}
              installers={installers}
              onAction={handleAction}
              onConfirmReceived={setReceivedOrder}
              onRefresh={() => refetch()}
            />
          )}
        />
      )}
      <ReceivedDialog
        order={receivedOrder}
        installers={installers}
        onAction={handleAction}
        onClose={() => setReceivedOrder(null)}
        onSent={() => {
          setReceivedOrder(null);
          refetch();
        }}
      />
    </div>
  );
};

OperationsPage.path = "/operaciones";
